import React, { KeyboardEventHandler } from 'react';
import {
  StylesConfig,
  GroupBase,
  MultiValueProps,
  components,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { MAX_EMAIL_INVITATIONS_PER_ORGANIZATION } from 'utils/constants';
import * as yup from 'yup';
import { OverlayTrigger } from 'react-bootstrap';
import styles from './index.module.scss';

const selectStyles: StylesConfig<string, true, GroupBase<string>> = {
  control: (base) => ({
    display: 'flex',
    minHeight: '50px',
    border: '1px solid var(--gray2)',
    borderRadius: '5px',
    backgroundColor: base.backgroundColor,
  }),
  multiValue: (base, { isDisabled }) => {
    const invalidValueStyles = {
      border: '1px solid var(--danger-dark5)',
      backgroundColor: 'var(--danger-dark5-background)',
    };

    const styles = {
      ...base,
      borderRadius: 5,
      backgroundColor: 'var(--light16)',
      fontWeight: 600,
      fontFamily: 'roboto',
      color: 'var(--tooltip-gray)',
    };

    if (isDisabled)
      return {
        ...styles,
        ...invalidValueStyles,
      };

    return styles;
  },
};

type EmailsFieldProps = {
  onChange: (inputEmails: { emails: string[]; inputValue: string }) => void;
  emails: string[];
  inputValue: string;
  emailsToIgnore: string[];
  invalidEmails: string[];
};

const EmailsField = ({
  inputValue,
  onChange,
  emails,
  emailsToIgnore,
  invalidEmails,
}: EmailsFieldProps) => {
  const handleOnKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!inputValue) return;

    switch (event.key) {
      case 'Enter':
      case 'Tab':
        event.preventDefault();

        if (!yup.string().email().isValidSync(inputValue)) return;

        if (!emailsToIgnore.includes(inputValue) && inputValue !== '')
          onChange({ emails: [...emails, inputValue], inputValue: '' });

        break;
    }
  };

  return (
    <>
      <OverlayTrigger
        placement="top"
        show={inputValue.length > 0}
        overlay={({ ref, show, style }) => (
          <div
            ref={ref}
            hidden={!show}
            style={style}
            role="tooltip"
            className={styles.tooltipContainer}
          >
            <div className={styles.tooltip}>
              You must press Return after typing each email address.
            </div>
          </div>
        )}
      >
        <div>
          <CreatableSelect
            placeholder={'Type emails...'}
            aria-label="Emails"
            components={{
              DropdownIndicator: null,
              MultiValue: function MultiValue(
                props: MultiValueProps<string, true, GroupBase<string>> & {
                  data: string;
                }
              ) {
                return (
                  <components.MultiValue
                    {...props}
                    isDisabled={invalidEmails.includes(props.data)}
                  />
                );
              },
            }}
            inputValue={inputValue}
            value={emails}
            onKeyDown={handleOnKeyDown}
            onInputChange={(newValue) => {
              if (emails.length < MAX_EMAIL_INVITATIONS_PER_ORGANIZATION)
                onChange({
                  emails,
                  inputValue: newValue.trim(),
                });
            }}
            getOptionLabel={(value) => value}
            onChange={(_, { removedValue }) => {
              const updatedEmails = emails.filter(
                (email) => email !== removedValue
              );
              onChange({
                emails: updatedEmails,
                inputValue,
              });
            }}
            styles={selectStyles}
            menuIsOpen={false}
            isClearable={false}
            isMulti
          />
        </div>
      </OverlayTrigger>
    </>
  );
};

export default EmailsField;
